import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';
import { Icon } from '../../../components/Icon.js';
import { ApiError } from '../../../lib/api.js';

type GroupBy = 'day' | 'week' | 'type' | 'cashier' | 'currency';

export function CashboxReports() {
  const role = useRole();
  const canReport = canDoCashbox(role, 'report');
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exportBusy, setExportBusy] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const params: Record<string, string | undefined> = {};
  if (from) params['from'] = from;
  if (to) params['to'] = to;

  const { data: grouped, isLoading } = useQuery({
    queryKey: ['cashbox', 'reports', 'grouped', groupBy, from, to],
    queryFn: () => cashboxApi.getGroupedReport(groupBy, params),
    enabled: canReport,
  });

  const { data: dashboard } = useQuery({
    queryKey: ['cashbox', 'dashboard', from, to],
    queryFn: () => cashboxApi.getDashboard(params),
    enabled: canReport,
  });

  const telegramSend = useMutation({
    mutationFn: () => cashboxApi.sendDailyReport(params),
  });

  async function onExport(format: 'sheets' | 'flat') {
    setExportBusy(true);
    setExportErr(null);
    try {
      await cashboxApi.exportCsv(format, `cashbox-${format}-${new Date().toISOString().slice(0, 10)}.${format === 'sheets' ? 'zip' : 'csv'}`);
    } catch (e) {
      setExportErr(e instanceof ApiError ? (e.body?.message ?? e.message) : String(e));
    } finally {
      setExportBusy(false);
    }
  }

  const fmt = (n?: number) =>
    n !== undefined ? Math.round(n).toLocaleString('ru-RU') : '—';

  if (!canReport) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
        Доступ к отчётам не разрешён для вашей роли.
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>С</label>
          <input className="input-admin" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>По</label>
          <input className="input-admin" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Группировка</label>
          <select
            className="input-admin"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            data-testid="groupby-select"
          >
            <option value="day">По дням</option>
            <option value="week">По неделям</option>
            <option value="type">По типу</option>
            <option value="cashier">По кассиру</option>
            <option value="currency">По валюте</option>
          </select>
        </div>
      </div>

      {/* Server hit the full-scan safety valve — сводка ниже неполная */}
      {dashboard?.truncated && (
        <div className="cb-truncated-banner" data-testid="cb-truncated-banner">
          ⚠️ Показаны не все операции — сузьте период
        </div>
      )}

      {/* Summary KPIs */}
      {dashboard && (
        <div className="kpi-grid" style={{ marginBottom: 16 }} data-testid="reports-kpi">
          <div className="kpi-card">
            <p className="kpi-label">Нал (приход)</p>
            <p className="kpi-value">{fmt(dashboard.cashIncome)} GEL</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Безнал (приход)</p>
            <p className="kpi-value">{fmt(dashboard.transferIncome)} GEL</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Европа (приход)</p>
            <p className="kpi-value">{fmt(dashboard.europeIncome)} GEL</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Итог</p>
            <p className="kpi-value">{fmt(dashboard.balance)} GEL</p>
          </div>
        </div>
      )}

      {/* Grouped table */}
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">Сводка</p>
        </div>
        {isLoading && (
          <p style={{ padding: 32, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>Загрузка…</p>
        )}
        {!isLoading && (
          <table className="data" data-testid="reports-table">
            <thead>
              <tr>
                <th>Период / Группа</th>
                <th className="num">Приход</th>
                <th className="num">Расход</th>
                <th className="num">Нетто</th>
                <th className="num">Операций</th>
              </tr>
            </thead>
            <tbody>
              {(grouped ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
                    Данных нет
                  </td>
                </tr>
              )}
              {(grouped ?? []).map((row, i) => (
                <tr key={i}>
                  <td>{row.label}</td>
                  <td className="num tnum cb-income">{Math.round(row.income).toLocaleString('ru-RU')}</td>
                  <td className="num tnum cb-expense">{Math.round(row.expense).toLocaleString('ru-RU')}</td>
                  <td className={`num tnum bold ${row.net >= 0 ? 'cb-income' : 'cb-expense'}`}>
                    {row.net >= 0 ? '+' : ''}{Math.round(row.net).toLocaleString('ru-RU')}
                  </td>
                  <td className="num">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Export */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-head">
          <p className="panel-title">Экспорт</p>
        </div>
        <div className="f-row" style={{ gap: 8, flexWrap: 'wrap', padding: '0 0 4px' }}>
          <button
            className="btn-admin"
            onClick={() => onExport('sheets')}
            disabled={exportBusy}
            data-testid="export-sheets-btn"
          >
            <Icon name="download" size={14} />
            {exportBusy ? 'Готовлю…' : 'Скачать CSV (формат Sheets)'}
          </button>
          <button
            className="btn-admin"
            onClick={() => onExport('flat')}
            disabled={exportBusy}
            data-testid="export-flat-btn"
          >
            <Icon name="download" size={14} />
            Скачать CSV (плоский)
          </button>
          <button
            className="btn-admin"
            onClick={() => telegramSend.mutate()}
            disabled={telegramSend.isPending}
          >
            <Icon name="bell" size={14} />
            {telegramSend.isPending ? 'Отправляю…' : 'Отправить в Telegram'}
          </button>
        </div>
        {exportErr && (
          <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginTop: 8 }}>Ошибка: {exportErr}</p>
        )}
      </div>
    </div>
  );
}
